import React, { useMemo } from 'react';
import { createContext } from '@shared/utils/createContext';
import { Placement } from '@withjoy/joykit/components/PopoverV2/Popover.types';

export type StepHandle = Readonly<{
  stepName: string;
  tourName: string;
  node: HTMLElement | null;
  getRect: () => DOMRect | null;
  observe: (cb: () => void) => () => void;
  tooltip: React.ReactNode;
  placement?: Placement;
  borderRadius?: number;
}>;

export type TourRegistryContextValue = Readonly<{
  // Tour identity
  tourName: string;

  // Registry
  registerStep: (stepName: string, tourName: string, handle: StepHandle) => void;
  unregisterStep: (stepName: string, tourName: string) => void;
}>;

export type TourStateProps = Readonly<{
  // Tour state
  isOpen: boolean;
  currentStepName: string | null;
  currentStepIndex: number;
  orderedStepNames: ReadonlyArray<string>;
  isCollapsed: boolean;

  // Controls
  run: () => void;
  stop: () => void;
  next: () => void;
  prev: () => void;
  setIsCollapsed: (isCollapsed: boolean) => void;
  hasNext: boolean;
  hasPrevious: boolean;
}>;

export type TourStateContextValue = Readonly<{
  // Registry Data (moved to state context to avoid re-rendering registry consumers)
  steps: ReadonlyMap<string, StepHandle>;
  stateProps: TourStateProps;
}>;

export type TourContextValue = TourRegistryContextValue &
  Readonly<{
    steps: ReadonlyMap<string, StepHandle>;
  }> &
  TourStateProps;

export const [TourRegistryProvider, useTourRegistry] = createContext<TourRegistryContextValue>({
  name: 'TourRegistryContext',
  strict: false,
});

export const [TourStateProvider, useTourState] = createContext<TourStateContextValue>({
  name: 'TourStateContext',
  strict: false,
});

/**
 * Composite provider that splits the context value into registry and state contexts
 */
export const TourManager: React.FC<{ value: TourContextValue; children: React.ReactNode }> = ({ value, children }) => {
  const { tourName, steps, registerStep, unregisterStep, ...rest } = value;
  const stateProps: TourStateProps = rest;

  // Memoize registry value to prevent unnecessary re-renders
  // This context is now extremely stable and only changes if the tour name or handler identities change
  const registryValue = useMemo(
    () => ({
      tourName,
      registerStep,
      unregisterStep,
    }),
    [tourName, registerStep, unregisterStep],
  );

  // This context updates frequently (on tour run, navigation, or step registration)
  const stateValue: TourStateContextValue = useMemo(
    () => ({
      steps,
      stateProps,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      steps,
      stateProps.isOpen,
      stateProps.currentStepName,
      stateProps.currentStepIndex,
      stateProps.orderedStepNames,
      stateProps.isCollapsed,
      stateProps.run,
      stateProps.stop,
      stateProps.next,
      stateProps.prev,
      stateProps.setIsCollapsed,
      stateProps.hasNext,
      stateProps.hasPrevious,
    ],
  );

  return (
    <TourRegistryProvider value={registryValue}>
      <TourStateProvider value={stateValue}>{children}</TourStateProvider>
    </TourRegistryProvider>
  );
};

/**
 * Legacy hook that combines both contexts
 */
export const useTour = () => {
  const tourRegistry = useTourRegistry();
  const state = useTourState();

  if (!tourRegistry) {
    throw new Error('useTour must be used within a TourRegistryContext');
  }

  if (!state) {
    throw new Error('useTour must be used within a TourStateContext');
  }

  return { tourRegistry, state };
};
